import { useState, useEffect, useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, TextField, Button, Box, List,
  ListItemText, Checkbox, FormControl, FormGroup, FormControlLabel, ListItemButton, useTheme,
  InputLabel, Select, MenuItem, SelectChangeEvent, OutlinedInput, Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Persona, ModulePermission, PutPersonaRequest, PostPersonaRequest } from '../../../../app/services/api/generated';
import LeavePageModal from '../../../../components/shared/dialogs/LeavePageModal';
import { UserContext } from '../../../../components/shared/useUser';
import { useTranslation } from 'react-i18next';
import { transformForTranslation } from './RolesBox';
import TriStateCircle from './TriStateCircle';
import { Trophy01 } from '../../../../assets';

type AccessLevel = 'tenant' | 'franchise';

interface Props {
  openModal: boolean;
  onCancel: () => void;
  persona?: Persona;
  onConfirm: (data: PutPersonaRequest | PostPersonaRequest) => void;
  modules: ModulePermission[];
}

const splitPermissionName = (name: string, moduleName: string) => name.replace(moduleName, '').replace(/([A-Z])/g, ' $1').trim();

const CreateUpdateRoleModal = ({ openModal, onCancel, persona, onConfirm, modules }: Props) => {
  const theme = useTheme();
  const { user, franchises: userFranchises } = useContext(UserContext);

  const userTenants = user?.tenants ?? [];
  const isTenantLevelUser = userTenants.length > 0;
  const [request, setRequest] = useState<PutPersonaRequest | PostPersonaRequest>({ description: '', franchiseName: '', personaRoles: [] });
  const [initialRequest, setInitialRequest] = useState<PutPersonaRequest | PostPersonaRequest>({ description: '', franchiseName: '', personaRoles: [] });
  const [showLeaveModal, setShowLeaveModal] = useState<boolean>(false);
  const [description, setDescription] = useState<string>(persona?.description ?? '');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [selectedSystem, setSelectedSystem] = useState<string | null>(null);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);

  const [accessLevel, setAccessLevel] = useState<AccessLevel>('franchise');
  const [selectedTenantIds, setSelectedTenantIds] = useState<number[]>([]);
  const [selectedFranchiseIds, setSelectedFranchiseIds] = useState<number[]>([]);
  const [initialAccessLevel, setInitialAccessLevel] = useState<AccessLevel>('franchise');
  const [initialTenantIds, setInitialTenantIds] = useState<number[]>([]);
  const [initialFranchiseIds, setInitialFranchiseIds] = useState<number[]>([]);

  const userPermissions = user?.permissions || [];

  const modulesWithUserPermissions = modules.filter(m =>
    m.permissions?.some(p => userPermissions.includes(p)),
  );
  const systems = Array.from(new Set(modulesWithUserPermissions.map(m => m.system || '')));
  const filteredModules = selectedSystem ? modulesWithUserPermissions.filter(m => m.system === selectedSystem) : [];
  const selectedModuleObj = filteredModules.find(m => m.module === selectedModule);
  const filteredPermissions = selectedModuleObj ? selectedModuleObj.permissions : [];
  const allowedPermissions = filteredPermissions?.filter(p => userPermissions.includes(p)) || [];

  const { t } = useTranslation('manageRoles');

  useEffect(() => {
    // Determine if persona uses new multi-select or legacy single-select
    const hasMultiFranchise = persona?.franchiseIds && persona.franchiseIds.length > 0;
    const hasMultiTenant = persona?.tenantIds && persona.tenantIds.length > 0;
    const personaTenantId = persona?.tenantId;
    const personaFranchiseName = persona?.franchiseName;

    let level: AccessLevel = 'franchise';
    let tenantIds: number[] = [];
    let franchiseIds: number[] = [];

    if (hasMultiTenant) {
      level = 'tenant';
      tenantIds = persona.tenantIds ?? [];
    } else if (hasMultiFranchise) {
      level = 'franchise';
      franchiseIds = persona.franchiseIds ?? [];
    } else if (personaTenantId && !personaFranchiseName) {
      level = 'tenant';
      tenantIds = [personaTenantId];
    } else if (personaFranchiseName) {
      level = 'franchise';
      const matchedFranchise = userFranchises.find(f => f.franchiseName === personaFranchiseName);
      if (matchedFranchise?.franchiseId) {
        franchiseIds = [matchedFranchise.franchiseId];
      }
    }

    const filteredRoles = (persona?.personaRoles ?? []).filter(p => p != null && p !== '');
    const newRequest = {
      description: persona?.description,
      franchiseName: persona?.franchiseName,
      personaRoles: filteredRoles,
      franchiseIds: franchiseIds,
      tenantIds: tenantIds,
    };

    setRequest(newRequest);
    setInitialRequest(newRequest);
    setDescription(persona?.description ?? '');
    setSelectedPermissions(filteredRoles);

    setAccessLevel(level);
    setInitialAccessLevel(level);
    setSelectedTenantIds(tenantIds);
    setInitialTenantIds(tenantIds);
    setSelectedFranchiseIds(franchiseIds);
    setInitialFranchiseIds(franchiseIds);
  }, [persona, userFranchises]);

  useEffect(() => {
    if (openModal) {
      initializeAccessLevel();
    }
  }, [openModal]);

  useEffect(() => {
    if (systems && systems.length > 0 && !selectedSystem) {
      setSelectedSystem(systems[0]);
    }
  }, [systems, selectedSystem]);

  useEffect(() => {
    if (filteredModules.length > 0 && !selectedModule) {
      setSelectedModule(filteredModules[0].module || '');
    }
  }, [filteredModules, selectedModule]);

  const initializeAccessLevel = () => {
    if (persona) {
      return;
    }
    if (isTenantLevelUser && userTenants && userTenants.length > 0) {
      setAccessLevel('tenant');
      setSelectedTenantIds(userTenants[0].id ? [userTenants[0].id] : []);
      setSelectedFranchiseIds([]);
    } else if (userFranchises.length > 0) {
      setAccessLevel('franchise');
      setSelectedFranchiseIds(userFranchises[0].franchiseId ? [userFranchises[0].franchiseId] : []);
      setSelectedTenantIds([]);
    }
  };

  const isFormDirty = () => {
    return (
      description !== initialRequest.description ||
      JSON.stringify(selectedPermissions.sort()) !== JSON.stringify((initialRequest.personaRoles ?? []).sort()) ||
      accessLevel !== initialAccessLevel ||
      JSON.stringify([...selectedTenantIds].sort()) !== JSON.stringify([...initialTenantIds].sort()) ||
      JSON.stringify([...selectedFranchiseIds].sort()) !== JSON.stringify([...initialFranchiseIds].sort())
    );
  };

  const handleCancel = () => {
    if (isFormDirty()) {
      setShowLeaveModal(true);
    } else {
      onCancel();
      setRequest({ description: '', franchiseName: '', personaRoles: [] });
      setDescription('');
      setSelectedPermissions([]);
      setAccessLevel('franchise');
      setSelectedTenantIds([]);
      setSelectedFranchiseIds([]);
    }
  };

  const handlePermissionChange = (permission: string, checked: boolean) => {
    setSelectedPermissions(prev => {
      let updated = [...prev];
      if (checked && !updated.includes(permission)) {
        updated.push(permission);
      } else if (!checked && updated.includes(permission)) {
        updated = updated.filter(p => p !== permission);
      }
      return updated;
    });
  };

  const handleConfirm = () => {
    onConfirm({
      ...request,
      description: description,
      tenantId: undefined,
      franchiseName: undefined,
      tenantIds: accessLevel === 'tenant' && selectedTenantIds.length > 0 ? selectedTenantIds : undefined,
      franchiseIds: accessLevel === 'franchise' && selectedFranchiseIds.length > 0 ? selectedFranchiseIds : undefined,
      personaRoles: selectedPermissions.filter(p => p != null && p !== ''),
    });
    setRequest({ description: '', franchiseName: '', personaRoles: [] });
    setShowLeaveModal(false);
    setDescription('');
    setSelectedPermissions([]);
    setAccessLevel('franchise');
    setSelectedTenantIds([]);
    setSelectedFranchiseIds([]);
    onCancel();
  };

  const isCurrentUserPersona = persona && user && user.persona?.id === persona.id;
  const hasValidAccessSelection = accessLevel === 'tenant' ? selectedTenantIds.length > 0 : selectedFranchiseIds.length > 0;
  const isValid = description && description.trim() !== '' && selectedPermissions.length > 0 && !isCurrentUserPersona && hasValidAccessSelection;

  const getModuleTriState = (module: ModulePermission): 'all' | 'some' | 'none' => {
    if (!module.permissions || module.permissions.length === 0) return 'none';
    const userPermsInModule = module.permissions.filter(p => userPermissions.includes(p));
    const total = userPermsInModule.length;
    const selected = userPermsInModule.filter(p => selectedPermissions.includes(p)).length;
    if (selected === 0) return 'none';
    if (selected === total) return 'all';
    return 'some';
  };

  const getSystemTriState = (system: string): 'all' | 'some' | 'none' => {
    const systemModules = modulesWithUserPermissions.filter(m => m.system === system);
    let total = 0;
    let selected = 0;
    systemModules.forEach(module => {
      if (module.permissions && module.permissions.length > 0) {
        const userPermsInModule = module.permissions.filter(p => userPermissions.includes(p));
        total += userPermsInModule.length;
        selected += userPermsInModule.filter(p => selectedPermissions.includes(p)).length;
      }
    });
    if (total === 0 || selected === 0) return 'none';
    if (selected === total) return 'all';
    return 'some';
  };

  const handleTenantChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    setSelectedTenantIds(typeof value === 'string' ? value.split(',').map(Number) : value);
  };

  const handleFranchiseChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    setSelectedFranchiseIds(typeof value === 'string' ? value.split(',').map(Number) : value);
  };

  const getTenantName = (id: number) => {
    return userTenants.find(t => t.id === id)?.name ?? id.toString();
  };

  const getFranchiseName = (id: number) => {
    return userFranchises.find(f => f.franchiseId === id)?.franchiseName ?? id.toString();
  };

  return (
    <>
      <Dialog
        maxWidth='lg'
        fullWidth
        onClose={handleCancel}
        open={openModal}
        sx={{
          '& .MuiDialog-paper': {
            py: '18px',
            px: '24px',
            maxHeight: '95vh',
          },
        }}
      >
        <Box>
          <DialogTitle sx={{
            fontSize: theme.typography.textLG.fontSize,
            fontWeight: theme.typography.fontWeightBold,
            px: 0,
          }}>
            {t('createUpdateRole')}
          </DialogTitle>
          <Typography variant="textMD"
            component="div"
            sx={{ mb: 4 }}>
            {t('editAddRole')}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.custom.gray[800],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{
          flexGrow: 1,
          padding: 0,
          overflow: 'hidden',
        }}>
          <Box component='div'
            display='flex'
            flexDirection='row'
            alignItems='center'
            gap={2}
            py='12px'>
            <TextField
              required
              id="role-name-input"
              label={t('name')}
              onChange={(e) => setDescription(e.target.value)}
              inputProps={{ maxLength: 50 }}
              size='small'
              name='description'
              value={description}
              sx={{
                width: '320px',
                '& .MuiInputBase-root': {
                  height: '40px',
                  borderRadius: '4px',
                },
              }}
            />
            {isTenantLevelUser && (
              <>
                <FormControl size="small"
                  sx={{ minWidth: 150 }}>
                  <InputLabel id="access-level-label">{t('accessLevel')}</InputLabel>
                  <Select
                    labelId="access-level-label"
                    id="access-level-select"
                    value={accessLevel}
                    label={t('accessLevel')}
                    onChange={(e: SelectChangeEvent) => {
                      const newLevel = e.target.value as AccessLevel;
                      setAccessLevel(newLevel);
                      if (newLevel === 'tenant' && userTenants.length > 0) {
                        setSelectedTenantIds(userTenants[0].id ? [userTenants[0].id] : []);
                        setSelectedFranchiseIds([]);
                      } else if (newLevel === 'franchise' && userFranchises.length > 0) {
                        setSelectedFranchiseIds(userFranchises[0].franchiseId ? [userFranchises[0].franchiseId] : []);
                        setSelectedTenantIds([]);
                      }
                    }}
                  >
                    <MenuItem value="tenant">{t('tenantRole')}</MenuItem>
                    <MenuItem value="franchise">{t('franchiseRole')}</MenuItem>
                  </Select>
                </FormControl>
                {accessLevel === 'tenant' && (
                  <FormControl size="small"
                    sx={{ minWidth: 300 }}>
                    <InputLabel id="tenant-select-label">{t('selectTenants')}</InputLabel>
                    <Select
                      labelId="tenant-select-label"
                      id="tenant-select"
                      multiple
                      value={selectedTenantIds}
                      onChange={handleTenantChange}
                      input={<OutlinedInput label={t('selectTenants')} />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((id) => (
                            <Chip key={id}
                              label={getTenantName(id)}
                              size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {userTenants.map((tenant) => (
                        <MenuItem key={tenant.id}
                          value={tenant.id}>
                          <Checkbox checked={selectedTenantIds.includes(tenant.id ?? 0)} />
                          <ListItemText primary={tenant.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {accessLevel === 'franchise' && (
                  <FormControl size="small"
                    sx={{ minWidth: 300 }}>
                    <InputLabel id="franchise-select-label">{t('selectFranchises')}</InputLabel>
                    <Select
                      labelId="franchise-select-label"
                      id="franchise-select"
                      multiple
                      value={selectedFranchiseIds}
                      onChange={handleFranchiseChange}
                      input={<OutlinedInput label={t('selectFranchises')} />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((id) => (
                            <Chip key={id}
                              label={getFranchiseName(id)}
                              size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {userFranchises.map((franchise) => (
                        <MenuItem key={franchise.franchiseId}
                          value={franchise.franchiseId}>
                          <Checkbox checked={selectedFranchiseIds.includes(franchise.franchiseId ?? 0)} />
                          <ListItemText primary={franchise.franchiseName} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
            {!isTenantLevelUser && userFranchises.length > 0 && (
              <FormControl size="small"
                sx={{ minWidth: 300 }}>
                <InputLabel id="franchise-only-select-label">{t('selectFranchises')}</InputLabel>
                <Select
                  labelId="franchise-only-select-label"
                  id="franchise-only-select"
                  multiple
                  value={selectedFranchiseIds}
                  onChange={handleFranchiseChange}
                  input={<OutlinedInput label={t('selectFranchises')} />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => (
                        <Chip key={id}
                          label={getFranchiseName(id)}
                          size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {userFranchises.map((franchise) => (
                    <MenuItem key={franchise.franchiseId}
                      value={franchise.franchiseId}>
                      <Checkbox checked={selectedFranchiseIds.includes(franchise.franchiseId ?? 0)} />
                      <ListItemText primary={franchise.franchiseName} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 4,
            flexGrow: 1,
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant='textSM'>{t('systems')}</Typography>
              <Box sx={{ overflow: 'auto', maxHeight: '280px' }}>
                <List sx={{ flexGrow: 1 }}>
                  {systems.map(system => (
                    <ListItemButton
                      key={system}
                      selected={selectedSystem === system}
                      onClick={() => {
                        setSelectedSystem(system);
                        setSelectedModule(null);
                      }}
                    >
                      <TriStateCircle
                        state={getSystemTriState(system)}
                      />
                      <ListItemText primary={t(transformForTranslation(system))} />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant='textSM'>{t('modules')}</Typography>
              <Box sx={{ overflow: 'auto', maxHeight: '280px' }}>
                <List sx={{ flexGrow: 1 }}>
                  {filteredModules.map(m => (
                    <ListItemButton
                      key={m.module}
                      selected={selectedModule === m.module}
                      onClick={() => setSelectedModule(m.module || '')}
                    >
                      <TriStateCircle
                        state={getModuleTriState(m)}
                      />
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            {t(transformForTranslation(m.module || ''))}
                            {m.premium && (
                              <Trophy01 style={{ width: 16, height: 16, color: theme.palette.warning.main }} />
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant='textSM'>{t('permissions')}</Typography>
              <Box sx={{ overflow: 'auto', maxHeight: '280px' }}>
                {selectedModuleObj ? (
                  <FormControl component="fieldset"
                    sx={{ width: '100%', flexGrow: 1 }}>
                    <FormGroup sx={{ flexGrow: 1 }}>
                      {allowedPermissions.length > 0 ? allowedPermissions.map(permission => (
                        <FormControlLabel
                          key={permission}
                          control={
                            <Checkbox
                              checked={selectedPermissions.includes(permission)}
                              onChange={(_, checked) => handlePermissionChange(permission, checked)}
                            />
                          }
                          label={t(splitPermissionName(permission, selectedModuleObj?.module || '').toLowerCase()).replace(/\b\w/g, char => char.toLocaleUpperCase())}
                        />
                      )) : (
                        <Typography variant="textMD"
                          sx={{ p: 2, color: 'text.secondary' }}>
                          {t('noPermissions')}
                        </Typography>
                      )}
                    </FormGroup>
                  </FormControl>
                ) : (
                  <Typography variant="textMD"
                    sx={{ p: 2, color: 'text.secondary' }}>
                    {t('moduleSelection')}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          {isCurrentUserPersona && (
            <Box sx={{ mb: 2 }}>
              <Typography color="error"
                variant="textMD">
                {t('cannotEditOwnRole', 'You cannot update or delete a role you are currently assigned to.')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button variant='secondary'
            size="lg"
            onClick={handleCancel}
            disabled={isCurrentUserPersona || false}
          >
            {t('cancel')}
          </Button>
          <Button
            variant='primary'
            size="lg"
            disabled={!isValid}
            onClick={handleConfirm}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
      <LeavePageModal
        open={showLeaveModal}
        onConfirmUrl=""
        onClose={() => setShowLeaveModal(false)}
        onConfirm={() => setShowLeaveModal(false)}
        onCancel={() => {
          setShowLeaveModal(false);
          onCancel();
          setRequest({ description: '', franchiseName: '', personaRoles: [] });
          setDescription('');
          setSelectedPermissions([]);
        }}
      />
    </>
  );
};

export default CreateUpdateRoleModal;